// Donation dashboard — the treasurer-facing post-onboarding view of the Stripe
// Connect donation portal. Money is integer cents; dates are ISO.

import type { StripeConnectPanelState } from '../generated/enums.gen';
import type { DonationDisplayStatus } from '../generated/enums.gen';
import type { ErrorCode } from '../generated/errorCodes.gen';
import type { DonationHealthDto, StripePayoutDto, StripePayoutScheduleDto } from '../generated/dtos.gen';

/**
 * A status tab for the "View all" donation feed. `all` is unfiltered; the other three select
 * exactly the gifts whose derived {@link DonationDisplayStatus} equals the tab. There is NO
 * "Failed" tab — a failed charge is never persisted. Lowercase to ride the `?status=` query value.
 */
export type DonationStatusTab = 'all' | 'succeeded' | 'refunded' | 'disputed';

/**
 * Stable error `code` values the refund endpoint returns in its `{ error, code }` body. `insufficient_balance`
 * (the donation already paid out) routes the treasurer to the support hatch; `refund_failed` is a distinct
 * generic Stripe failure. Any other/absent code is treated as a generic failure by the client.
 */
export type RefundErrorCode = typeof ErrorCode.insufficient_balance | typeof ErrorCode.refund_failed;

// ---------------------------------------------------------------------------
// Live-only Stripe panels (balance + payouts). Unlike everything above, these
// come from a LIVE connected-account read (the `Stripe-Account` header), never
// our DB, and are never persisted (ADR-011). They load async behind a skeleton
// so the page — and the deadline-critical health banner — never blocks on Stripe.
// ---------------------------------------------------------------------------

/**
 * A Stripe payout's lifecycle status, passed through verbatim (lowercase). `pending`
 * and `in_transit` are the two "on the way to the bank" states that drive the
 * next-payout hero; `paid` has landed; `canceled`/`failed` did not.
 */
export type StripePayoutStatus =
  | 'paid'
  | 'pending'
  | 'in_transit'
  | 'canceled'
  | 'failed';

const STRIPE_PAYOUT_STATUSES = new Set<string>(['paid', 'pending', 'in_transit', 'canceled', 'failed'] satisfies StripePayoutStatus[]);

/** The API passes Stripe's payout status through as a plain string; this narrows it to the values we have copy for. */
export function isStripePayoutStatus(status: string): status is StripePayoutStatus {
  return STRIPE_PAYOUT_STATUSES.has(status);
}

/** The derived next-payout figure for the hero — a single in-flight payout. */
export interface NextPayout {
  arrivalDate: string;
  amountCents: number;
}

/**
 * The next expected payout is NOT a Stripe field — it is derived (per the spec): the
 * earliest-arriving payout that is still on its way to the bank (`pending` or
 * `in_transit`). Returns null when none are in flight, in which case the hero shows
 * the connected account's actual payout schedule rather than a made-up estimate.
 */
export function deriveNextPayout(
  payouts: readonly StripePayoutDto[],
): NextPayout | null {
  const inFlight = payouts.filter(
    (p) => p.status === 'pending' || p.status === 'in_transit',
  );
  if (inFlight.length === 0) {
    return null;
  }
  const earliest = inFlight.reduce((soonest, p) =>
    p.arrivalDate < soonest.arrivalDate ? p : soonest,
  );
  return { arrivalDate: earliest.arrivalDate, amountCents: earliest.amountCents };
}

/**
 * Whether payouts to the bank are stopped — the one derivation behind every
 * "is our money moving?" element in both products: the web dashboard's health
 * banner and live money cards, and the mobile Donations screen's status line,
 * verification banner and next-payout card.
 *
 * Shared because the two products sit in the same treasurer's day: two answers to
 * one question about their bank account is a bug that only ever surfaces as a
 * support ticket.
 *
 * `panelState` is null only before an account exists, and both dashboards render
 * post-onboarding — so the defensive fallback is the loud state, never a silent
 * all-clear.
 */
export function arePayoutsPaused(health: DonationHealthDto): boolean {
  const state: StripeConnectPanelState = health.panelState ?? 'ActionNeeded';
  return !health.payoutsEnabled || state === 'PastDue' || state === 'Restricted';
}

/**
 * The treasurer-facing sentence for the connected account's actual payout cadence
 * — what both the web dashboard and the mobile Donations screen show in place of
 * a next payout when none is in flight. Never an estimate: it describes the
 * schedule Stripe reports, or nothing.
 *
 * Shared rather than written per product because a treasurer sees both surfaces
 * in the same day, and a cadence worded two ways reads as two different answers.
 */
export function payoutScheduleCopy(schedule: StripePayoutScheduleDto): string {
  switch (schedule.interval) {
    case 'daily':
      return 'Payouts run daily, automatically.';
    case 'weekly': {
      const days = joinWithAnd(schedule.weeklyPayoutDays.map(capitalizeDay));
      return days
        ? `Payouts run weekly on ${days}, automatically.`
        : 'Payouts run weekly, automatically.';
    }
    case 'monthly': {
      const days = joinWithAnd(schedule.monthlyPayoutDays.map(ordinal));
      return days
        ? `Payouts run monthly on the ${days}, automatically.`
        : 'Payouts run monthly, automatically.';
    }
    case 'manual':
      return 'Payouts are sent manually.';
    default:
      // The API passes Stripe's interval through as a plain string.
      return 'Payouts run automatically.';
  }
}

function joinWithAnd(values: readonly string[]): string {
  if (values.length < 2) return values[0] ?? '';
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(', ')}, and ${values.at(-1)}`;
}

function capitalizeDay(day: string): string {
  return `${day.charAt(0).toUpperCase()}${day.slice(1)}`;
}

function ordinal(day: number): string {
  // 11th–13th break the last-digit rule ("11st" would be wrong).
  const tens = day % 100;
  if (tens >= 11 && tens <= 13) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
}
